import {createSlice} from '@reduxjs/toolkit'
import StatisticsService from '../../api/statistics'
import ResourceService from '../../api/resource'
import {addMinutes, addSeconds, addHours} from 'date-fns'
import moment from 'moment'
import {toast} from 'react-toastify'
import FormHelper from '../../utils/forms'
import {locationManager, regionKeys} from '../../utils/locations'
import {routes} from '../../routes'
import {doNothing} from '../../utils/placeholder'
import statusCodes from '../../utils/http'
import regionsData from '../../data/regions.json'

const secondsToISOString = (seconds) => {
    return addSeconds(new Date(), -(seconds + 60)).toISOString()
}

const types = {
    time: 'time',
    minute: 'minute',
    day: 'day',
    hour: 'hour'
}
const DEFAULT_LIMIT = 60

const MAX_LIMIT = 180

const DEFAULT_LOGS_LIMIT = 60
const DEFAULT_RESPONSE_LOGS_LIMIT = 100

const DIFFERENCE_IN_MIN = 1

const RESOURCES_VIEW_STATISTICS_COUNT = 18

const queryBuilder = (startDate, endDate, interval, limit) => {
    return `?start_date=${startDate}&end_date=${endDate}&interval=${interval}&limit=${limit}`
}

const getDefaultQuery = () => queryBuilder(secondsToISOString(3600), addMinutes(new Date(), -DIFFERENCE_IN_MIN).toISOString(), types.minute, DEFAULT_LIMIT)

const setLimitInQuery = (query, limit, index = 2, deleteCount = 2) => {
    const parts = query.split('&')
    parts.splice(index, deleteCount, `limit=${limit}`)
    return parts.join('&')
}

export const monitoringSlice = createSlice({
    name: 'monitoring',
    initialState: {
        isFetching: false,
        isStateChanged: false,
        resource: null,
        resourceLogs: [],
        responseLogs: [],
        data: [],
        categories: {},
        uptime: {},
        monitoring: {},
        additionalData: {},
        query: getDefaultQuery(),
        interval: types.minute,
        activeTimeIndex: 0,
        rangeSelected: false,
        getData: false,
        sslData: {},
        tldData: {},
        sslReminderData: {},
        tldReminderData: {},
        isSslTldDataFetching: false,
        selectRegions: [],
        showSectionLoader: true,
        responseDataIsFetching: false,
        isMonitoringLoading: false
    },
    reducers: {
        setMonitoring: (state, {payload}) => {
            const data = []
            const categories = []
            payload?.statistics?.map(item => {
                data.push(item?.duration)
                categories.push(state.interval === 'minute' || state.interval === 'hour' ?
                    moment(item?.checked_at).format('YYYY-MM-DDTHH:mm:ss[Z]') :
                    item?.checked_at)
            })
            state.uptime = payload?.uptime
            state.additionalData = {...state.additionalData, ...payload?.additional}
            state.data = data
            state.categories = categories
        },
        setResource: (state, {payload}) => {
            state.resource = payload
        },
        setIsStateChanged: (state, {payload}) => {
            state.isStateChanged = payload
        },
        setResourceLogs: (state, {payload}) => {
            state.resourceLogs = payload?.logs
            state.additionalData = {...state.additionalData, ...{state_changed_count: payload?.state_changed_count}}
        },
        setResponseLogs: (state, {payload}) => {
            state.responseLogs = payload
        },
        setIsFetching: (state, {payload}) => {
            state.isFetching = payload
        },
        setIsMonitoringLoading: (state, {payload}) => {
            state.isMonitoringLoading = payload
        },
        setActiveTimeIndex: (state, {payload}) => {
            state.activeTimeIndex = payload
        },
        setQuery: (state, {payload}) => {
            const date = addMinutes(new Date(), -DIFFERENCE_IN_MIN).toISOString()
            const item = payload.item
            let query = ''
            if (payload.type === types.time) {
                state.interval = item.interval
                query = queryBuilder((item.date), date, item.interval, item.limit)
            } else {
                state.interval = payload?.interval || types.day
                query = queryBuilder(item.startDate, item.endDate, state.interval, item.limit <= MAX_LIMIT ? item.limit : MAX_LIMIT)
            }
            state.query = query
            state.getData = true
            state.showSectionLoader = payload?.showLoader
        },
        resetQuery: state => {
            state.activeTimeIndex = 0
            state.interval = types.minute
            state.query = getDefaultQuery()
            state.getData = false
        },
        setRangeSelected: (state, {payload}) => {
            state.rangeSelected = payload
        },
        setSslData: (state, {payload}) => {
            state.sslData = payload
        },
        setTldData: (state, {payload}) => {
            state.tldData = payload
        },
        setSslReminderData: (state, {payload}) => {
            state.sslReminderData = payload
        },
        setTldReminderData: (state, {payload}) => {
            state.tldReminderData = payload
        },
        setIsSslTldDataFetching: (state, {payload}) => {
            state.isSslTldDataFetching = payload
        },
        setSelectRegions: (state, {payload}) => {
            state.selectRegions = payload
        },
        setResponseDataIsFetching: (state, {payload}) => {
            state.responseDataIsFetching = payload
        }
    }
})

export const {
    setMonitoring,
    setResource,
    setResourceLogs,
    setResponseLogs,
    setIsFetching,
    setIsStateChanged,
    setQuery,
    resetQuery,
    setRangeSelected,
    setSslData,
    setTldData,
    setSslReminderData,
    setTldReminderData,
    setIsSslTldDataFetching,
    setSelectRegions,
    setResponseDataIsFetching,
    setIsMonitoringLoading,
    setActiveTimeIndex
} = monitoringSlice.actions

export default monitoringSlice.reducer

export const getResourceRegions = (id, navigate) => {
    return async dispatch => {
        try {
            const res = await ResourceService.getResourceRegions(id)
            const resourceRegions = res?.data?.data?.regions?.map(item => item?.region)
            const regions = locationManager.getMainLocations().filter(item => resourceRegions.includes(item.value))
            dispatch(setSelectRegions(regions?.map(item => {
                return {
                    value: item?.value,
                    label: item?.label
                }
            })))
            return regions
        } catch (e) {
            const res = e?.response
            if (statusCodes.SpecialErrors.includes(res?.status)) {
                navigate(routes.not_found.path)
            }
        }
    }
}

const endPoints = {
    [locationManager.allRegions[regionKeys.eu_central_1].value]: {
        resource: ResourceService.getResource,
        responseLogs: ResourceService.getResponseLogs,
        logs: StatisticsService.getStateChangedLogs,
        statistics: StatisticsService.getResourceStatistic
    },
    [locationManager.allRegions[regionKeys.az_baku_1].value]: {
        resource: ResourceService.getResource,
        logs: StatisticsService.getStateChangedLogs,
        responseLogs: ResourceService.getResponseLogs,
        statistics: StatisticsService.getResourceStatistic
    },
    [locationManager.allRegions[regionKeys.us_east_1].value]: {
        resource: ResourceService.getResourceUS,
        logs: StatisticsService.getStateChangedLogsUS,
        statistics: StatisticsService.getResourceStatisticUS
    },
    [locationManager.allRegions[regionKeys.ap_southeast_2].value]: {
        resource: ResourceService.getResourceAU,
        logs: StatisticsService.getStateChangedLogsAU,
        statistics: StatisticsService.getResourceStatisticAU
    },
    [locationManager.allRegions[regionKeys.ap_southeast_1].value]: {
        resource: ResourceService.getResourceAS,
        logs: StatisticsService.getStateChangedLogsAS,
        statistics: StatisticsService.getResourceStatisticAS
    }
}

export const getResource = (id, region) => {
    return async(dispatch) => {
        try {
            const {data} = await endPoints[region?.value].resource(id)
            dispatch(setResource(data?.data?.resource))
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
    }
}

export const getResourceWithChangedState = (id, region) => {
    return async(dispatch, getStates) => {
        try {
            const {data} = await endPoints[region?.value].resource(id)
            if (getStates()?.monitoring?.resource?.state !== data?.data?.resource?.state) {
                await dispatch(setResource(data?.data?.resource))
                await dispatch(setIsStateChanged(true))
                return
            }
            await dispatch(setIsStateChanged(false))
        } catch {
            doNothing()
        }
    }
}

export const getResourceData = (id, query, region, navigate, showLoader = false, isSelectedRegion = false, isAllRegionShowed = false) => {
    const logQuery = setLimitInQuery(query, DEFAULT_LOGS_LIMIT)
    const responseLogQuery = setLimitInQuery(query, DEFAULT_RESPONSE_LOGS_LIMIT)

    return async(dispatch, getStates) => {
        const regions = getStates().monitoring.selectRegions
        if (!regions?.length) {
            return
        }

        showLoader && dispatch(setIsMonitoringLoading(true))
        try {
            const [resourceStatistics, resourceLogs, responseLogs, resource] = await Promise.allSettled([
                endPoints[region].statistics(id, query),
                endPoints[region].logs(id, logQuery),
                endPoints[region].responseLogs
                    ? endPoints[region].responseLogs(id, responseLogQuery)
                    : Promise.resolve(null),
                endPoints[region].resource(id)
            ])
            dispatch(setResource(resource?.value?.data?.data?.resource))

            if (isAllRegionShowed) {
                const data = []
                const categories = []
                resourceStatistics?.value?.data?.data?.statistics?.map(item => {
                    data.push(item?.duration)
                    categories.push(getStates().monitoring.interval === 'minute' || getStates().monitoring.interval === 'hour' ?
                        moment(item?.checked_at).format('YYYY-MM-DDTHH:mm:ss[Z]') :
                        item?.checked_at)
                })

                isSelectedRegion && dispatch(setResourceLogs(resourceLogs?.value?.data?.data))
                isSelectedRegion && dispatch(setMonitoring(resourceStatistics?.value?.data?.data))
                return {
                    data,
                    categories,
                    region,
                    ...(regions?.some(region => region.value === regionsData[0].value)
                        ? {responseLogs: responseLogs?.value?.data?.data || []}
                        : {})}
            }

            dispatch(setResourceLogs(resourceLogs?.value?.data?.data))
            dispatch(setResponseLogs(responseLogs?.value ?
                responseLogs?.value?.data?.data || [] :
                null))
            dispatch(setMonitoring(resourceStatistics?.value?.data?.data))
        } catch (e) {
            const res = e?.response
            if (statusCodes.SpecialErrors.includes(res?.status)) {
                navigate(routes.not_found.path)
            }
        } finally {
            dispatch(setIsMonitoringLoading(false))
        }
    }
}

export const getAllResourcesStatisticsData = resources => {
    return async dispatch => {
        const date = new Date()
        const query = queryBuilder(addHours(date, -RESOURCES_VIEW_STATISTICS_COUNT).toISOString(), date.toISOString(), types.hour, RESOURCES_VIEW_STATISTICS_COUNT)
        dispatch(setResponseDataIsFetching(true))
        try {
            const promises = resources?.map(resource => {
                return endPoints[resource?.regions?.[0]].statistics(resource?.resource_id, query)
            })
            const res = await Promise.all(promises)
            const responseTimeData = res?.map((item, index) => {
                const data = item?.data?.data
                if (data) {
                    return {
                        max_duration: data?.additional?.max_duration % 1 ? Number(data?.additional?.max_duration)?.toFixed(0) : data?.additional?.max_duration,
                        avg_duration: data?.additional?.avg_duration % 1 ? Number(data?.additional?.avg_duration)?.toFixed(0) : data?.additional?.avg_duration,
                        statistics: data?.statistics?.map(item => {
                            return {
                                state: item?.state === 1,
                                checked_at: item?.checked_at,
                                duration: item?.duration % 1 ? Number(item?.duration)?.toFixed(0) : item?.duration
                            }
                        })
                    }
                }
                return null
            })
            dispatch(setResponseDataIsFetching(false))
            return responseTimeData
        } catch (e) {
            doNothing()
            dispatch(setResponseDataIsFetching(false))
        }
    }
}

export const getSslAndTldData = (id, showLoader = true) => {
    return async dispatch => {
        try {
            const [sslData, tldData, sslReminderData, tldReminderData] = await Promise.all([
                ResourceService.getResourceSsl(id),
                ResourceService.getResourceTld(id),
                ResourceService.getReminderData('ssl', id),
                ResourceService.getReminderData('tld', id)
            ])

            dispatch(setSslData(sslData?.data?.data?.ssl_data))
            dispatch(setTldData(tldData?.data?.data?.tld_data))
            dispatch(setSslReminderData(sslReminderData?.data?.data?.ssl_reminder))
            dispatch(setTldReminderData(tldReminderData?.data?.data?.tld_reminder))

            showLoader && dispatch(setIsSslTldDataFetching(false))
            return true
        } catch (e) {
            return false
        }
    }
}

export const addReminder = (type, id, data, setFieldError, closeModal) => {
    return async() => {
        try {
            const res = await ResourceService.addReminder(type, id, data)
            toast.success(res?.data?.message)
            closeModal()
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
    }
}

export const updateReminder = (type, id, data, setFieldError, closeModal) => {
    return async() => {
        try {
            const res = await ResourceService.updateReminder(type, id, data)
            toast.success(res?.data?.message)
            closeModal()
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
    }
}

export const deleteReminder = (type, id, closeModal) => {
    return async() => {
        try {
            const data = await ResourceService.deleteReminder(type, id)
            toast.success(data?.data?.message)
            closeModal()
            return true
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
    }
}
